<script setup lang="ts">
// NPM
import { nextTick, reactive, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';

// Components
import {
  BaseButton,
  BaseIcon,
  FieldRating,
  FieldTextarea,
  FieldFileUploader,
  FieldLabel,
  LinkedText,
} from '@magma-app/magma-lapilli';

// Types
import type { PropType } from 'vue';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';

// Stores
import { useCommonStore } from '@/stores/common';
import { useRelationsStore } from '@/stores/relations';
import { useUserStore } from '@/stores/user';
const commonStore = useCommonStore();
const relationsStore = useRelationsStore();
const userStore = useUserStore();

// Plugins
const route = useRoute();
const router = useRouter();

// Props
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  persona: {
    type: String as PropType<'prospect' | 'helper'>,
    default: 'helper',
  },
  needFeedbackRating: {
    type: Boolean,
    default: false,
  },
  needFeedbackComment: {
    type: Boolean,
    default: false,
  },
  needFeedbackFile: {
    type: Boolean,
    default: false,
  },
  relationMomentId: {
    type: Number,
    default: null,
  },
  instructionFeedback: {
    type: String,
    default: '',
  },
});

// Reactive variables
const root = ref<HTMLInputElement>();
const validatingMoment = ref(false);

const formData = reactive({
  comment: '',
  rating: null,
  file: null as File | null,
});

// Form validation
const v$ = useVuelidate(
  {
    rating: {
      requiredIf: requiredIf(() => props.needFeedbackRating),
    },
    comment: {
      requiredIf: requiredIf(() => props.needFeedbackComment),
    },
    file: {
      requiredIf: requiredIf(
        () => props.needFeedbackFile && props.persona === 'helper'
      ),
    },
  },
  formData
);

// Computed properties
const isButtonDisabled = computed(() => {
  // Disable button during API call
  if (validatingMoment.value) {
    return true;
  }

  // Check if rating is required and missing
  if (props.needFeedbackRating && !formData.rating) {
    return true;
  }
  // Check if comment is required and missing
  if (props.needFeedbackComment && !formData.comment.trim()) {
    return true;
  }
  // Check if file is required and missing (only for helper persona)
  if (props.needFeedbackFile && props.persona === 'helper' && !formData.file) {
    return true;
  }

  return false;
});

// Methods
const onCloseModal = async () => {
  commonStore.closeFeedbackRelationModal();
};

const onMarkAsDoneWithFeedback = async () => {
  if (validatingMoment.value) return;
  validatingMoment.value = true;
  try {
    v$.value.$touch();
    if (await v$.value.$validate()) {
      const payload = {
        ...(formData.comment ? { comment: formData.comment } : {}),
        ...(formData.rating ? { rating: formData.rating } : {}),
        ...(formData.file ? { file: formData.file } : {}),
        relationMomentId: props.relationMomentId,
      };

      if (props.persona === 'prospect') {
        await relationsStore.markRelationMomentAs({
          ...payload,
          role: 'done',
          type: 'helpee',
        });

        await relationsStore.getRelation({
          relationId: Number(route?.params.relationId),
          type: 'helpee',
        });
      } else if (props.persona === 'helper') {
        await relationsStore.markRelationMomentAs({
          ...payload,
          role: 'done',
          type: 'helper',
        });

        await Promise.all([
          relationsStore.getRelation({
            relationId: Number(route?.params.relationId),
            type: 'helper',
          }),
          // Refresh all relations to update the overall list
          await relationsStore.getRelations(),
          await userStore.getUser(),
        ]);
      }

      AnalyticsHelper.trackEvent(
        `validated_by_${
          route?.name === 'helper-relation-overview' ? 'helper' : 'helpee'
        }`,
        {
          email_validation:
            route?.query.helper === 'done' || route?.query.helpee === 'done'
              ? true
              : false,
          dashboard_validation:
            route?.query.helper === 'done' || route?.query.helpee === 'done'
              ? false
              : true,
          user_id: route?.params.relationId as string,
        }
      );

      commonStore.closeFeedbackRelationModal();

      await nextTick();

      v$.value.$reset();
      formData.rating = null;
      formData.comment = '';
      formData.file = null;
    }
  } catch (error) {
    console.error(error);
  } finally {
    validatingMoment.value = false;
  }
};
</script>

<template>
  <TransitionRoot as="template" :show="open" data-cy="modal">
    <Dialog
      :initial-focus="root"
      as="div"
      class="relative z-10"
      @close="onCloseModal"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-background-emphasis bg-opacity-70 backdrop-blur-md transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div ref="root" class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-[15px] bg-background-default text-left shadow-xl transition-all sm:w-full sm:max-w-lg p-6 flex flex-col gap-4"
            >
              <div class="flex items-start justify-between">
                <BaseIcon
                  class="max-w-fit border-8 border-background-brand-default"
                  :size="24"
                  bg-color="#E0EAFF"
                  color="#444CE7"
                  icon="star"
                />
                <button
                  type="button"
                  class="rounded-md bg-background-default text-foreground-subtle hover:text-border-subtle focus:outline-none focus:ring-2 focus:ring-border-default focus:ring-offset-2"
                  @click="onCloseModal"
                >
                  <span class="sr-only">Close</span>
                  <BaseIcon icon="close" color="#94A3B8" />
                </button>
              </div>

              <div class="flex flex-col gap-1">
                <h3
                  class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
                >
                  {{ $t('feedbackRelationModal.title') }}
                </h3>
                <!-- ONLY FOR AMBASSADOR -->
                <p
                  v-if="props.persona === 'helper'"
                  class="scrollbar-hide max-h-44 overflow-scroll whitespace-pre-line text-mgm-txt-sm font-normal text-foreground-default"
                >
                  <LinkedText :text="props.instructionFeedback" />
                </p>
              </div>

              <FieldRating
                v-if="props.needFeedbackRating"
                id="ratings"
                v-model="formData.rating"
                :label="$t('feedbackRelationModal.feedbackModalRating.label')"
                :ratings="[
                  {
                    emoji: '😒',
                    label: $t('feedbackRelationModal.feedbackModalRating.bad'),
                    value: 1
                  },
                  {
                    emoji: '🤩',
                    label: $t('feedbackRelationModal.feedbackModalRating.good'),
                    value: 2
                  }
                ]"
                :validation="v$.rating"
                required
              />

              <FieldTextarea
                v-if="props.needFeedbackComment"
                v-model="formData.comment"
                :label="$t('feedbackRelationModal.feedbackModalComment.label')"
                :placeholder="
                  $t('feedbackRelationModal.feedbackModalComment.placeholder')
                "
                :rows="4"
                :validation="v$.comment"
                data-cy="comment"
                required
              />

              <div v-if="props.needFeedbackFile">
                <!-- Always show label and description -->
                <div class="mb-2">
                  <div class="flex items-start">
                    <FieldLabel
                      for="file-required"
                      :label="
                        $t('feedbackRelationModal.feedbackModalFile.label')
                      "
                    />
                    <span
                      v-if="props.persona === 'helper'"
                      class="ml-1 inline-block align-top text-mgm-txt-sm font-medium text-foreground-brand-default"
                    >
                      *
                    </span>
                  </div>
                  <FieldLabel
                    :label="
                      $t('feedbackRelationModal.feedbackModalFile.description')
                    "
                    description
                  />
                </div>

                <FieldFileUploader
                  v-if="!formData.file"
                  id="file-required"
                  :required="props.persona === 'helper'"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,
                         video/mp4,video/quicktime,video/webm,video/x-matroska,video/x-m4v,
                         application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                         application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                         application/vnd.openxmlformats-officedocument.presentationml.presentation,
                         text/csv,text/plain,application/rtf,application/zip,
                         application/vnd.ms-excel,application/vnd.ms-word,application/vnd.ms-powerpoint"
                  @change="(file: File) => (formData.file = file)"
                />

                <div
                  v-if="formData.file"
                  class="p-3 rounded border border-border-subtle shadow-sm flex justify-between items-center"
                >
                  <div class="flex gap-3 items-center">
                    <div
                      class="w-10 h-10 rounded bg-background-subtle flex items-center justify-center"
                    >
                      <BaseIcon icon="file-check" color="#5F6C85" />
                    </div>
                    <div>
                      <p
                        class="text-mgm-txt-sm font-medium text-foreground-emphasis"
                      >
                        {{ formData.file.name }}
                      </p>
                      <span
                        v-if="formData.file.size > 1024"
                        class="text-mgm-txt-xs text-foreground-default"
                      >
                        {{
                          (Math.round(formData.file.size / 1024) * 100) / 100
                        }}ko
                      </span>
                    </div>
                  </div>
                  <button @click="formData.file = null">
                    <BaseIcon icon="trash" color="#5F6C85" />
                  </button>
                </div>
              </div>

              <BaseButton
                class="mt-2 w-full"
                :text="$t('generic.validate')"
                :disabled="isButtonDisabled"
                data-cy="btn-validate-moment"
                @click="onMarkAsDoneWithFeedback"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
